import { useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { arrayMoveImmutable } from 'utils/arrayMove';
import styled from 'styled-components';
import { FormField } from 'ui/formField';
import { GhostSuccessButton } from 'ui/button';
import { Tooltip } from 'ui/tooltip';
import { colors } from 'ui/theme';
import { Add16, NegativeFill } from 'ui/icons';
import { ButtonTitle } from 'modules/templateEditor/common/styles';
import {
  constructFieldErrors,
  FieldNames,
  SettingsReadOnlyText,
  TemplateEditViews,
} from '../../common';
import {
  PushContentDefinition,
  PushActionButton,
  PushActionButtonType,
  ActionIconType,
} from '../../../graphql/types';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { ButtonTypeVariations } from './ButtonTypeVariations';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { constructErrorInlineText } from '../../common/ErrorInlineText';

const StyledReadOnlyText = styled(SettingsReadOnlyText)`
  overflow-wrap: anywhere;
`;

const StyledAddIcon = styled(Add16)`
  margin-right: ${({ theme }) => theme.spacing.threeQuarter};
`;

const defaultActionButton = {
  identifier: '',
  title: SETTINGS_DISPLAY_CONTENT.actionButtons.defaultButtonTitle,
  buttonType: PushActionButtonType.default,
  action: {
    type: '',
    data: '',
  },
  openApp: true,
  requiresUnlock: false,
  inputPlaceholder: '',
  inputTitle: '',
  actionIcon: {
    iconType: ActionIconType.systemImage,
    imageName: '',
  },
};

export const ActionButtons = () => {
  const { view } = useQueryParamContext();
  const {
    isEditMode: isEditModeContext,
    liveTemplate,
    setCurrentMediumField,
  } = useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const pushData = (liveTemplate?.mediumData || {}) as PushContentDefinition;
  const actionButtonList = (pushData.actionButtons || []) as PushActionButton[];
  const isEditMode = view === TemplateEditViews.preview || isEditModeContext;
  const MAX_NUMBER_OF_BUTTONS = 3;

  const setActionButtonList = (list: PushActionButton[]) =>
    setCurrentMediumField('actionButtons', list);

  const handleAddParam = () => {
    setActionButtonList([...actionButtonList, defaultActionButton]);
  };

  const sortActionButtonList = useCallback(
    (dragEndObj: any) => {
      const sourceIndex = dragEndObj.source.index;
      const destinationIndex = dragEndObj.destination?.index;

      if (typeof sourceIndex === 'number' && typeof destinationIndex === 'number') {
        const newArrayList = arrayMoveImmutable(actionButtonList, sourceIndex, destinationIndex);
        setActionButtonList(newArrayList);
      }
    },
    [actionButtonList],
  );

  const getButtonTitles = (index: number, title: string) => {
    return index + 1 !== actionButtonList.length ? `${title},` : title;
  };

  const { hasError: hasActionButtonError } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.ACTION_BUTTONS,
  });

  const actionButtonsErrorText = constructErrorInlineText({
    condition: isEditMode,
    hasError: hasActionButtonError,
  });

  const readOnlyText = pushData.isSilentPush
    ? SETTINGS_DISPLAY_CONTENT.deliverSilently.silenced
    : SETTINGS_DISPLAY_CONTENT.unsetText;

  return (
    <FormField
      isDisabled={pushData.isSilentPush}
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.actionButtons.labelText}
      hasError={hasActionButtonError}
      errorText={actionButtonsErrorText}
    >
      {isEditMode ? (
        <Tooltip
          isEnabled={pushData.isSilentPush}
          tooltipContent={SETTINGS_DISPLAY_CONTENT.deliverSilently.restriction}
        >
          <DragDropContext onDragEnd={sortActionButtonList}>
            <Droppable droppableId="action-buttons-droppable">
              {(droppableProvided) => (
                <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                  {actionButtonList.map((value: PushActionButton, index: number) => (
                    <Draggable key={`item-${index}`} draggableId={`item-${index}`} index={index}>
                      {(draggableProvided) => (
                        <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                          <ButtonTypeVariations
                            index={index}
                            value={value}
                            draggableProvided={draggableProvided}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <GhostSuccessButton
            onClick={handleAddParam}
            isDisabled={
              pushData.actionButtons?.length === MAX_NUMBER_OF_BUTTONS || pushData.isSilentPush
            }
          >
            <StyledAddIcon />
            {SETTINGS_DISPLAY_CONTENT.actionButtons.buttonText}
          </GhostSuccessButton>
        </Tooltip>
      ) : (
        <StyledReadOnlyText
          isUnset={!pushData.defaultAction}
          $hasError={hasActionButtonError}
          data-test="action-button-ro"
        >
          {!pushData.isSilentPush && pushData.actionButtons && pushData.actionButtons.length > 0 ? (
            <>
              {hasActionButtonError && <NegativeFill color={colors.red100} />}
              {pushData.actionButtons.map((button: PushActionButton, i) => (
                <ButtonTitle key={i} $hasError={hasActionButtonError}>
                  {getButtonTitles(i, button.title)}
                </ButtonTitle>
              ))}
            </>
          ) : (
            readOnlyText
          )}
        </StyledReadOnlyText>
      )}
    </FormField>
  );
};
